import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useBranch } from '~/contexts/BranchContext';
import type { MainCategory } from '~/types/new_types';
import { getCategoryDescription, getCategoryName } from '~/utils/categoryHelper';

interface CategoryHeaderProps {
	category: MainCategory
}

const CategoryHeader: React.FC<CategoryHeaderProps> = ({ category }) => {
	const { t, i18n } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const isRTL = i18n.language === 'ar';
	const {branch} = useBranch()

	const hasDescription = !!getCategoryDescription(category, i18n.language);

	return (
		<motion.div
			className="mb-6 relative"
			initial={{ opacity: 0}}
			animate={{ opacity: 1 }}
		>
			<div
				className="flex flex-col items-center gap-4 p-6 overflow-hidden"
				style={{
					backgroundImage: branch?.theme.headerStyle == 'cover' ? (category.image_url
						? `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${category.image_url})`
						: `linear-gradient(to right, ${branch?.theme.primaryColor}20, ${branch?.theme.primaryColor}05)`)
						: 'none',
					backgroundColor: branch?.theme.headerStyle == 'solid'
						? `${branch?.theme.primaryColor}`
						: 'transparent',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<motion.div
					initial={{ scale: 0.9, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					transition={{ delay: 0.2 }}
					className="flex-shrink-0 w-16 h-16 flex items-center justify-center text-white overflow-hidden"
					style={{
						backgroundColor: `${branch?.theme.primaryColor}80`,
						backdropFilter: 'blur(4px)',
						borderRadius: branch?.theme.borderRadius
					}}
				>
					{
						category.image_url ?
							<div
								className="w-full h-full bg-cover bg-center"
								style={{
									backgroundImage: `url(${category.image_url})`,
								}}
							/>
							:
							<div>
								{/* This could be an icon specific to the category */}
								<span className="text-2xl">🍽️</span>
							</div>
					}
				</motion.div>

				<div className='flex flex-col items-center'>
					<div className="flex items-center gap-2">
						<motion.h2
							className={`${branch?.theme.titleFontSize} ${branch?.theme.boldTitles ? 'font-semibold' : ''} ${branch?.theme.headerStyle !== 'transparent' ? 'text-white' : branch?.theme.textColor}`}
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.3 }}
						>
							{getCategoryName(category, i18n.language)}
						</motion.h2>

						{hasDescription && (
							<motion.button
								onClick={() => setIsExpanded(!isExpanded)}
								className={`p-1 rounded-full transition-all duration-200 cursor-pointer ${branch?.theme.headerStyle !== 'transparent'
										? 'text-white/80 hover:text-white hover:bg-white/10'
										: `${branch?.theme.textColor}80 hover:bg-gray-100 dark:hover:bg-gray-800`
									}`}
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ delay: 0.4 }}
								whileHover={{ scale: 1.1 }}
								whileTap={{ scale: 0.95 }}
								aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
							>
								{isRTL ? (
									isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
								) : (
									isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
								)}
							</motion.button>
						)}
					</div>

					<AnimatePresence>
						{isExpanded && hasDescription && (
							<motion.div
								initial={{ opacity: 0, height: 0}}
								animate={{ opacity: 1, height: 'auto' }}
								exit={{ opacity: 0, height: 0 }}
								transition={{
									duration: 0.2,
									ease: "easeInOut"
								}}
								className="overflow-hidden"
							>
								<motion.p
									className={`mt-2 text-sm text-center ${branch?.theme.headerStyle !== 'transparent'
											? 'text-white/80'
											: `${branch?.theme.textColor}80`
										}`}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ delay: 0.1 }}
								>
									{getCategoryDescription(category, i18n.language)}
								</motion.p>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</motion.div>
	);
}

export default CategoryHeader